/**
 * Reading site settings and collections out of published content — **with no React and no HTTP**
 * (`CMS-014`, `CMS-015`).
 *
 * ## Why these are functions rather than field reads at the call site
 *
 * Every one of them answers the same question twice: *what did the editor say*, and *what does
 * the site show when they said nothing*. `CMS-014` AC-3 is that second half — a page renders with
 * sensible defaults when a setting is empty — and it is the half that quietly stops being true
 * when the answer lives inline in a component. A footer that reads `settings.footerGroups.map(…)`
 * is one empty field away from an empty footer, and nobody notices until the site has one.
 *
 * So the fallback is part of the reader, in one place, and it is unit-testable because nothing
 * here imports the `@/` alias or a component.
 *
 * ## What is deliberately not defaulted
 *
 * **Social links.** There is no sensible default for "where is H2HSpark on LinkedIn" — a guess
 * would be a link to somebody else's page. An empty list renders no social row at all, which is
 * the honest outcome.
 *
 * The defaults that do exist are navigation labels and footer headings: chrome a visitor needs to
 * move around a site, short enough to be a component's vocabulary rather than its copy. Sentences
 * belong in the CMS, and `the-marketing-site-holds-no-copy.test.ts` is what keeps them there.
 */

import {
  siteSettingOr,
  type SiteFooterGroup,
  type SiteSocialLink,
} from '@h2hspark/contracts';

import type { ContentData } from './types';
import { list, text } from './types';

/**
 * The footer's readers live in `@h2hspark/contracts` now, because the organizer entry page draws
 * the same footer (`ORG-001` AC-5). Re-exported under the names this app has always imported.
 */
export {
  DEFAULT_CONTACT_EMAIL_ADDRESS,
  DEFAULT_FOOTER_GROUPS,
  DEFAULT_SITE_NAME,
  siteContactEmailAddress,
  siteContactLocation,
  siteFooterGroups,
  siteFooterNote,
  siteFooterText,
  siteName,
  siteSocialLinks,
} from '@h2hspark/contracts';

export interface NavigationItem {
  readonly label: string;
  readonly href: string;
  /**
   * How the header draws it (`siteSettings.navigation[].style`).
   *
   * `''` is "the editor did not say", which is **not** the same as `'link'`: the header's older
   * rule — the last entry is the primary action — applies only to entries that said nothing, so a
   * navigation authored before the field existed renders exactly as it did. A footer link is
   * always a link and ignores this.
   *
   * Optional so the defaults above, and the footer's groups, can be written without it — they are
   * links, and saying so three times would be noise.
   */
  readonly style?: string;
}

export type FooterGroup = SiteFooterGroup;

export type SocialLink = SiteSocialLink;

/** Where a case study's own page lives (`CMS-015` AC-2). One place, so the card and the route agree. */
export const CASE_STUDY_PATH_PREFIX = '/case-studies';

export function caseStudyPath(slug: string): string {
  return `${CASE_STUDY_PATH_PREFIX}/${slug}`;
}

/**
 * The header links a visitor gets when nobody has set any (`MEM-043` AC-6).
 *
 * `MEM-SHL-010` names what a visitor with no account is met by: the wordmark, marketing
 * navigation, **Log in** and **Get started**. The wordmark and the navigation are
 * `site-chrome-panel.tsx`'s; the two actions are these, and they are the *defaults* rather than
 * fixed chrome because the header is content (ADR 022) and an editor may rewrite it.
 *
 * That is worth being explicit about: this list is what the site shows when the CMS has said
 * nothing, and an editor who deletes both actions has deleted a member's route into the product.
 * It is a content decision with a content decision's consequences, and the criterion is met by
 * the shipped default rather than by the code refusing to render what was published.
 *
 * **Order matters.** `SiteHeaderChrome` draws the last entry as the primary button, so Get
 * started is last: it is the action the marketing site exists to produce, and Log in is the one
 * the small number of people who already have an account are looking for.
 *
 * `/app/join` rather than `/app` for Get started — `MEM-002` puts account creation at the top of
 * the registration wizard, and sending a new visitor to Discover makes them find it.
 */
export const DEFAULT_NAVIGATION: ReadonlyArray<NavigationItem> = [
  { label: 'For organizers', href: '/for-organizers', style: '' },
  { label: 'Log in', href: '/app/login', style: '' },
  { label: 'Get started', href: '/app/join', style: '' },
];

/**
 * **The two account actions**, recognised by where they go rather than by what they are called
 * (`MEM-043` AC-8, TW 44145091).
 *
 * The header is content, so *Log in* and *Get started* are labels an editor may rewrite in any
 * language they like — matching on the words would be a rule that breaks the first time somebody
 * types *Sign in*. Where they go cannot be rewritten without ceasing to be these actions at all:
 * `/app/login` is the member app's sign-in door and `/app/join` is `MEM-001`'s wizard. A published
 * entry pointing at either **is** an account action, whatever it says on it.
 *
 * Matched on the path alone, ignoring a query or a fragment, because `Get started` on
 * `/for-organizers` is overridden to a destination with one on it.
 */
export const MEMBER_ACCOUNT_ACTION_PATHS = ['/app/login', '/app/join'] as const;

export function isMemberAccountAction(href: string): boolean {
  const path = href.split(/[?#]/u)[0]?.replace(/\/+$/u, '') ?? '';
  return MEMBER_ACCOUNT_ACTION_PATHS.some((account) => path === account);
}

/**
 * **Whether an entry leads into the member app** — `/app` or anything under it. The header draws
 * such an entry with the account actions rather than the pages even when it is plain text, which
 * is what *Log in* is (TW 44164674). Path only, as above.
 */
export function isMemberAppPath(href: string): boolean {
  const path = href.split(/[?#]/u)[0]?.replace(/\/+$/u, '') ?? '';
  return path === '/app' || path.startsWith('/app/');
}

/**
 * **What a signed-in member is offered instead** — one way into the app (`MEM-043` AC-8).
 *
 * Sumit, 2026-09-21: *"the header will show a signed-in member one way into the app plus their
 * avatar, instead of 'Log in' and 'Get started'."* The avatar is the other half and is
 * `member/member-avatar.tsx`'s; this is the action beside it.
 *
 * `/app` rather than `/app/account`, and the two differ on purpose: the avatar goes to *Your
 * account*, as it does in the member app, and the action goes to the app's front door. A member
 * reading a marketing page wants the product, not their settings.
 *
 * Drawn as the primary pill, which is the slot *Get started* was occupying — the site's one filled
 * action stays one filled action, so the header does not change shape when you sign in.
 */
export const OPEN_THE_APP_ACTION: NavigationItem = {
  label: 'Open the app',
  href: '/app',
  style: 'primary',
};

/**
 * The site navigation as a **signed-in member** sees it (`MEM-043` AC-8, TW 44145091, TW 44145257).
 *
 * The two account actions are replaced by a single {@link OPEN_THE_APP_ACTION}, **in the place the
 * first of them held**, and every other entry is left exactly as published: *For organizers* is
 * not an account action and a member may well still want it. That is the whole of the change — the
 * published content still supplies the links, and only the doors a member has already walked
 * through are swapped.
 *
 * Signed out, this returns the navigation untouched, which is `MEM-043` AC-6 and the prototype's
 * only drawing of this header.
 *
 * **An editor who has deleted both actions gets nothing inserted.** The rule is *in place of* the
 * account actions, not *in addition to* them: putting a control the editor removed back onto a
 * public page would be the code overruling published content, which `CMS-014` is built not to do.
 * The avatar is still drawn, and it is still a way into the app.
 */
export function navigationForSession(
  navigation: ReadonlyArray<NavigationItem>,
  signedIn: boolean,
): ReadonlyArray<NavigationItem> {
  if (!signedIn) return navigation;
  if (!navigation.some((item) => isMemberAccountAction(item.href))) return navigation;
  let replaced = false;
  const applied: NavigationItem[] = [];
  for (const item of navigation) {
    if (!isMemberAccountAction(item.href)) {
      applied.push(item);
      continue;
    }
    if (replaced) continue;
    replaced = true;
    applied.push(OPEN_THE_APP_ACTION);
  }
  return applied;
}

/**
 * **The prefix an editor types to reach the organizer console** (`ORG-001` AC-1, ruling of
 * 2026-09-11: the organizer call to action must actually arrive at organizer sign-in and sign-up).
 *
 * ## The problem this solves
 *
 * `apps/organizer` is a **separate application on a separate origin** — `/organizer` behind the
 * CDN locally and in every deployed environment, `http://localhost:5203` on a dev machine — and
 * which one it is, is a build-time value (`VITE_ORGANIZER_URL`, `env.ts`). A CMS href is a string
 * an editor typed, stored once and served to every environment. The two cannot meet: a seeded
 * `http://localhost:5203/start` is wrong everywhere but one laptop, and a seeded `/start` is a
 * marketing-site route that does not exist and renders the CMS 404.
 *
 * That is why the prototype's own *Organizer Login* goes nowhere: it is a link with no
 * environment behind it (`handoff-corrections.md` #133).
 *
 * So content names the **destination** rather than the address: `organizer:/start` and
 * `organizer:/login`, resolved here against whatever this build's organizer origin is. The editor
 * keeps the link; the deployment keeps the hostname.
 *
 * Only the two paths the console publishes as public entry points are accepted. Anything else
 * resolves to `null` and `ContentLink` renders the label as plain text — the same answer it gives
 * a refused `javascript:` URL, and for the same reason: a link into a console route that needs a
 * session is a link to a sign-in redirect wearing another name.
 */
export const ORGANIZER_LINK_PREFIX = 'organizer:';

/** The console paths content may address. `apps/organizer`'s `PUBLIC_PATHS.entry` and `.login`. */
export const ORGANIZER_LINK_PATHS = ['/start', '/login'] as const;

/**
 * Resolve an `organizer:` address against this build's organizer origin.
 *
 * Returns `null` for anything that is not one — including an unknown path — so the caller can tell
 * "not an organizer link" from "an organizer link I cannot honour" only by asking first. Callers
 * pass every href through {@link isOrganizerLink} first, which keeps this total.
 */
export function resolveOrganizerLink(href: string, organizerUrl: string): string | null {
  if (!href.startsWith(ORGANIZER_LINK_PREFIX)) return null;
  const path = href.slice(ORGANIZER_LINK_PREFIX.length);
  if (!ORGANIZER_LINK_PATHS.includes(path as (typeof ORGANIZER_LINK_PATHS)[number])) return null;
  // Trailing slashes trimmed off the base, as `organizerConsoleUrl` does on the member footer:
  // `VITE_ORGANIZER_URL` is typed by a human per environment and arrives both ways.
  return `${organizerUrl.replace(/\/+$/u, '')}${path}`;
}

export function isOrganizerLink(href: string): boolean {
  return href.startsWith(ORGANIZER_LINK_PREFIX);
}

/**
 * What one page changes about the site header (`marketingPage.navigationOverrides`).
 *
 * See that field's own note for why a page says this rather than the settings carrying a second
 * navigation. Applied by {@link navigationForPage}.
 */
export interface NavigationOverride {
  /** The label of the entry to change, matched against the published navigation. */
  readonly replaces: string;
  /** Each optional, and absent means *keep what the entry had* — the seed omits what it keeps. */
  readonly label?: string;
  readonly href?: string;
  readonly style?: string;
  readonly remove?: boolean;
}

export function pageNavigationOverrides(
  page: ContentData | undefined,
): ReadonlyArray<NavigationOverride> {
  if (page === undefined) return [];
  return list(page, 'navigationOverrides')
    .map((entry) => ({
      replaces: text(entry, 'replaces'),
      label: text(entry, 'label'),
      href: text(entry, 'href'),
      style: text(entry, 'style'),
      remove: entry['remove'] === true,
    }))
    .filter((entry) => entry.replaces !== '');
}

/**
 * The site navigation as **this page** draws it.
 *
 * Each override replaces an entry in place, so the cluster keeps its order — a page that renames
 * one control does not reshuffle the other five. An override naming a label the navigation does
 * not have is **ignored**: a stale edit should leave the header as published rather than append a
 * control nobody reviewed to a public page.
 *
 * An empty `label`, `href` or `style` means *keep what the entry had*, so a page can change only
 * the destination — which is what `/for-organizers` does to *Get started*.
 */
export function navigationForPage(
  navigation: ReadonlyArray<NavigationItem>,
  overrides: ReadonlyArray<NavigationOverride>,
): ReadonlyArray<NavigationItem> {
  if (overrides.length === 0) return navigation;
  const byLabel = new Map(overrides.map((entry) => [entry.replaces, entry]));
  const applied: NavigationItem[] = [];
  for (const item of navigation) {
    const override = byLabel.get(item.label);
    if (override === undefined) {
      applied.push(item);
      continue;
    }
    if (override.remove === true) continue;
    const kept = (given: string | undefined, own: string): string =>
      given === undefined || given === '' ? own : given;
    applied.push({
      label: kept(override.label, item.label),
      href: kept(override.href, item.href),
      style: kept(override.style, item.style ?? ''),
    });
  }
  return applied;
}

export function siteTagline(settings: ContentData | undefined): string {
  return valueOr(settings, 'tagline', '');
}

/**
 * The header's links, in the editor's order (`CMS-014` AC-5).
 *
 * The order is the list's order and nothing else — not alphabetical, not by any field. A
 * navigation bar is a sequence somebody chose, and re-sorting it here would silently overrule
 * them.
 *
 * An entry missing a label or a link is dropped rather than rendered as a blank: a half-filled row
 * in the editor is work in progress, and putting an empty link in the header is worse than
 * omitting it.
 */
export function siteNavigation(settings: ContentData | undefined): ReadonlyArray<NavigationItem> {
  const items = navigationItemsIn(settings, 'navigation');
  return items.length > 0 ? items : DEFAULT_NAVIGATION;
}

/**
 * A collection section's items, capped by the editor's limit (`CMS-015` AC-3).
 *
 * The order arriving here is already the editor's — the delivery API sorts by the field the
 * content type declares, which is `sortOrder` for both collections — so this only trims. Sorting
 * again on this side would be a second opinion about an order the API already settled, and the
 * two would disagree the first time one changed.
 *
 * A limit of zero, a negative one, or anything that is not a number means "no limit". Treating a
 * malformed limit as zero would empty a section that an editor believed they had filled.
 */
export function limitedItems<T>(items: ReadonlyArray<T>, limit: unknown): ReadonlyArray<T> {
  if (typeof limit !== 'number' || !Number.isFinite(limit) || limit <= 0) return items;
  return items.slice(0, Math.floor(limit));
}

/**
 * Initials for a testimonial with no photo, or for the organization on a case study card.
 *
 * Derived rather than authored: `content-types.ts` drops the prototype's `initials` field on
 * purpose, because a name and its initials are one fact and storing both lets them disagree.
 *
 * **A one-word name takes its first two letters** — "Uplift" is "UP", not "U". A person's name has
 * two words and a organization's often has one, and this function serves both: a case study card
 * for GreenHouse drew a single "G" in a 40px tile, which reads as a rendering failure rather than
 * as a monogram. This is the rule `event-discovery`'s `initialsOfName` already follows for exactly
 * the same avatar; two spellings of "initials of an organization" on one site is how the same
 * organizer ends up with two different monograms on two different screens.
 *
 * The prototype's own authored value for GreenHouse is "GH" rather than "GR", because a human read
 * the internal capital as a word boundary. Deriving that would mean this function and the events
 * one disagreeing about the same name, which is the worse of the two mismatches — so the deviation
 * is deliberate and recorded, not an oversight.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return '';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  const first = words[0]?.[0] ?? '';
  const last = words[words.length - 1]?.[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

/**
 * A testimonial's attribution line — the name, then whatever else is known about them.
 *
 * Built here rather than in the component so that the *empty* cases are decided once: a
 * testimonial with a name and nothing else must not render a dangling comma, and one with an
 * organization but no role must not render an empty gap where the role would have been.
 */
export function attributionOf(testimonial: ContentData): string {
  const name = text(testimonial, 'name');
  const detail = attributionDetailOf(testimonial);
  return detail === '' ? name : `${name}, ${detail}`;
}

/**
 * The part of the attribution that is not the name — "Programs Director, GreenHouse".
 *
 * Its own function rather than something the card derives by slicing the full string: a name that
 * happens to contain a comma would make that slice cut in the wrong place, and the bug would only
 * appear for the one person whose name has one in it.
 */
export function attributionDetailOf(testimonial: ContentData): string {
  return [text(testimonial, 'role'), text(testimonial, 'organizationName')]
    .filter((part) => part !== '')
    .join(', ');
}

function navigationItemsIn(
  data: ContentData | undefined,
  key: string,
): ReadonlyArray<NavigationItem> {
  if (data === undefined) return [];
  return list(data, key)
    .map((item) => ({
      label: text(item, 'label'),
      href: text(item, 'href'),
      // Absent reads as `''`, which the header treats as "the editor did not say" — see
      // `NavigationItem.style`. Footer groups read this too and ignore it: a footer link is a link.
      style: text(item, 'style'),
    }))
    .filter((item) => item.label !== '' && item.href !== '');
}

function valueOr(settings: ContentData | undefined, key: string, fallback: string): string {
  return siteSettingOr(settings, key, fallback);
}

/**
 * The footer's organizer pitch, as `Web App.dc.html` places it — inside the footer block, after
 * the link columns and above the copyright rule.
 *
 * Returns `null` when nothing is authored, so a site that has not set it gets no empty panel.
 */
export function siteFooterPromo(settings: ContentData | undefined): {
  readonly eyebrow: string;
  readonly headline: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly ctaHref: string;
  readonly secondaryLabel: string;
  readonly secondaryHref: string;
} | null {
  const headline = valueOr(settings, 'footerPromoHeadline', '');
  if (headline === '') return null;
  return {
    eyebrow: valueOr(settings, 'footerPromoEyebrow', ''),
    headline,
    body: valueOr(settings, 'footerPromoBody', ''),
    ctaLabel: valueOr(settings, 'footerPromoCtaLabel', ''),
    ctaHref: valueOr(settings, 'footerPromoCtaHref', ''),
    secondaryLabel: valueOr(settings, 'footerPromoSecondaryLabel', ''),
    secondaryHref: valueOr(settings, 'footerPromoSecondaryHref', ''),
  };
}
